import {makeAutoObservable, observable} from 'mobx'
import {Lang} from 'yougile-lang'
import {ObjUtil, Time, UIUtil} from 'yougile-util'
import {
  DataObjectId,
  Department,
  Hub,
  PermissionRoleKey,
  SandboxTree,
  User,
  UserAccount
} from 'yougile-model'
import {Accounts} from '@client/accounts'
import {CompanyPreferencesClient} from '@client/company-preferences-client'
import {DepartmentsClient} from '@client/departments-client'
import {DirectMessages} from '@client/direct-messages'
import {EventBus} from '@client/event-bus'
import {openInvitePopup} from '@client/events'
import {PermissionsChecker} from '@client/permissions-checker'
import {Sync} from '@client/sync'
import {Tracking, TrackingEvent} from '@common/tracking'
import {showDeleteConfirm} from '@components/common/base/notifications'
import {MessageBox} from '@components/message-box'
import {createUseStore} from '@containers/hooks'
import {ChatUtils} from '@extensions/chat-utils'
import {globals} from '@extensions/globals-base'
import {Project} from '@extensions/project'
import {UserProxy} from '@extensions/proxies/user-proxy'
import {UserAccess} from '@extensions/user-access'

interface State {
  onCompanyChangeListener: number

  show: boolean
  wait: boolean
  user: UserProxy
  changed: boolean

  projects: Hub[]
  chosenProjects: {[id: string]: PermissionRoleKey}

  departments: Department[]
  chosenDepartments: {[id: string]: 'manager' | 'member'}
  menuOpenForDepartmentId: DataObjectId | undefined

  positionTitle: string
  positionEdit: boolean
  positionEditStart: string
}

export class UserCardStore {
  state: State
  box: SandboxTree

  constructor() {
    this.box = Sync.getCurrentSandbox()

    this.state = {
      onCompanyChangeListener: globals.onCompanyChange(
        () => (this.box = Sync.getCurrentSandbox())
      ),
      show: false,
      wait: false,
      user: undefined,
      changed: false,

      positionTitle: '',
      positionEdit: false,
      positionEditStart: undefined,

      departments: [],
      chosenDepartments: {},
      menuOpenForDepartmentId: undefined,
      projects: [],
      chosenProjects: {}
    }

    makeAutoObservable(
      this,
      {
        state: observable.shallow,
        box: false
      },
      {autoBind: true}
    )

    EventBus.on(openInvitePopup, data => {
      this.state.user = UserProxy.create(data.user)
      this.state.show = true
      this.state.changed = false
      this.initData()
    })
  }

  initData(): void {
    if (!this.state.show) {
      return
    }
    this.state.wait = false
    const {user} = this.state
    if (!user) {
      return
    }
    this.state.departments = DepartmentsClient.listDepartments(this.box)
    this.state.chosenDepartments = this.getDepartmentsForUser(user.userId)
    this.state.projects = Project.listProjects(
      this.box.companyId,
      'not-archive'
    ).sort(Project.projectOrderComparer)
    this.state.chosenProjects = this.getProjectsForUser(user.userId)
    this.positionUpdate()
  }

  dispose(): void {
    globals.removeCompanyListener(this.state.onCompanyChangeListener)
  }

  upChanged(): void {
    const {changed, user, show} = this.state
    if (!show || !changed || !user) {
      return
    }
    if (user) {
      const chosenProjects = this.getProjectsForUser(user.userId)
      const chosenDeps = this.getDepartmentsForUser(user.userId)
      const oldPosition = this.getPositionForUser(user.userId)

      if (
        this.state.positionTitle === oldPosition &&
        ObjUtil.deepEqual(this.state.chosenDepartments, chosenDeps, false) &&
        ObjUtil.deepEqual(this.state.chosenProjects, chosenProjects, false)
      ) {
        this.state.changed = false
      }
    }
  }

  canSeeEmails(): boolean {
    return CompanyPreferencesClient.isAllowedForCurrentUser(
      this.box.companyId,
      'seeEmails'
    )
  }

  positionInit(te: HTMLInputElement): void {
    this.state.positionEditStart = te.value
  }

  positionCanEdit(): boolean {
    const {user} = this.state
    const companyId = this.box.companyId
    const whoCan = CompanyPreferencesClient.get(companyId).editPosition
    const currUserId = globals.currentUserId
    const isAdmin = PermissionsChecker.isAdmin()

    if (isAdmin) {
      return true
    }
    if (whoCan === 'self') {
      return currUserId === user.userId
    } else if (whoCan === 'managers') {
      const deps = DepartmentsClient.listDepartments(this.box)
      return !!deps.find(d => d.managers.includes(currUserId))
    } else {
      return false
    }
  }

  positionStartEdit(): void {
    if (!this.positionCanEdit()) {
      return
    }
    this.state.positionEdit = true
  }

  positionKeyup(te: React.KeyboardEvent<HTMLInputElement>): void {
    const el = te.target as HTMLInputElement
    if (!el) {
      return
    }
    this.state.positionTitle = el.value
    this.state.changed = true
    this.upChanged()
  }

  positionUpdate(): void {
    const {user} = this.state
    this.state.positionTitle = this.getPositionForUser(user.userId)
    this.state.positionEdit = false
  }

  positionCancel(): void {
    this.positionUpdate()
  }

  positionSubmit(te: {text: string}): void {
    this.state.positionTitle = te.text
    this.state.positionEdit = false
  }

  close(): boolean {
    this.state.show = false
    this.state.wait = false
    this.state.changed = false
    this.state.positionEdit = false
    this.savePosition()
    return true
  }

  private savePosition(): void {
    const {user} = this.state
    if (!user) {
      return
    }
    if (this.state.positionEdit) {
      this.state.positionTitle = this.state.positionEditStart
      this.state.positionEdit = false
    }
    const c = this.box.get(this.box.companyId) as Hub
    if (!c.data.employeeSettings) {
      c.data.employeeSettings = {}
    }
    const oldSett = c.data.employeeSettings[user.userId] || {
      displayName: '',
      position: ''
    }
    if (oldSett.position !== this.state.positionTitle.trim()) {
      c.data.employeeSettings[user.userId] = {
        ...oldSett,
        position: this.state.positionTitle.trim()
      }
      c.commit()
      void Tracking.send(TrackingEvent.depsSetPosition)
    }
  }

  getInfoStatus(): string {
    let infoStatus = Lang.msg.userOnline
    const account = Accounts.get(this.state.user.userId)
    if (account.status === 'offline') {
      infoStatus = account.lastActivity
        ? Lang.msg.userOfflineSeenAt + Time.formatDateTime(account.lastActivity)
        : Lang.msg.userOffline
    } else if (account.status === 'online' && UserAccount.isInDnd(account)) {
      infoStatus = Lang.msg.userDnd + Time.formatDateTime(account.dndUntil)
    }
    return infoStatus
  }

  toggleAdmin(): Promise<void> {
    return Accounts.toggleAdmin(this.box.companyId, this.state.user.userId)
  }

  async deleteUser(): Promise<void> {
    if (!PermissionsChecker.isAdmin()) {
      MessageBox.show('Operation is unavailable', 'danger')
    } else {
      const allAdmins = this.box
        .getAllUsers(false)
        .filter(
          (u: User) => (this.box.users.get(u.id) || {isAdmin: false}).isAdmin
        )
      if (
        allAdmins.length === 1 &&
        allAdmins[0].id === this.state.user.userId
      ) {
        MessageBox.show(Lang.msg.removeLastAdminWarn, 'danger')
        return
      }
      if (await showDeleteConfirm(Lang.msg.deleteUser)) {
        UserAccess.removeUserFromCompany(this.state.user.userId, this.box)
        this.close()
      }
    }
  }

  openChat(): void {
    this.close()
    ChatUtils.openChat(DirectMessages.requestChatWith(this.state.user.userId))
  }

  toggleDepMenu(depId: DataObjectId, e?: React.MouseEvent): void {
    if (e && UIUtil.isConsumed(e)) {
      return
    }
    this.state.menuOpenForDepartmentId = depId
  }

  chooseDepRole(depId: DataObjectId, role: 'manager' | 'member'): void {
    if (!PermissionsChecker.isAdmin()) {
      MessageBox.warn(Lang.msg.permission.depEditWarn)
      return
    }
    this.state.chosenDepartments = {
      ...this.state.chosenDepartments,
      [depId]: role
    }
    this.state.changed = true
    this.upChanged()
  }

  toggleDep(depId: DataObjectId, e?: React.MouseEvent): void {
    const {chosenDepartments} = this.state
    if (!PermissionsChecker.isAdmin()) {
      e?.preventDefault()
      MessageBox.warn(Lang.msg.permission.depEditWarn)
      return
    }
    if (chosenDepartments[depId]) {
      delete chosenDepartments[depId]
    } else {
      chosenDepartments[depId] = 'member'
    }
    this.state.chosenDepartments = {
      ...this.state.chosenDepartments
    }
    this.state.changed = true
    this.upChanged()
  }

  setRole(projectId: DataObjectId, role: PermissionRoleKey): void {
    const project = this.box.getHub(projectId)
    if (
      UserAccess.getRoleInProject(globals.currentUserId, project) !== 'admin' &&
      !PermissionsChecker.isAdmin()
    ) {
      MessageBox.warn(Lang.msg.permission.noEditObject)
      return
    }
    this.state.chosenProjects = {
      ...this.state.chosenProjects,
      [project.id]: role
    }
    this.state.changed = true
    this.upChanged()
  }

  toggleProject(project: Hub): void {
    if (
      UserAccess.getRoleInProject(globals.currentUserId, project) !== 'admin' &&
      !PermissionsChecker.isAdmin()
    ) {
      MessageBox.warn(Lang.msg.permission.noEditObject)
      return
    }
    if (this.state.chosenProjects[project.id]) {
      delete this.state.chosenProjects[project.id]
    } else {
      this.state.chosenProjects[project.id] = 'worker'
    }
    this.state.chosenProjects = {...this.state.chosenProjects}
    this.state.changed = true
    this.upChanged()
  }

  submit(): void {
    const {chosenProjects, chosenDepartments} = this.state
    const {changed, user, wait} = this.state
    if (wait || !changed) {
      return
    }
    const wasChosen = this.getProjectsForUser(user.userId)
    const ids = ObjUtil.getChangedKeys(
      wasChosen,
      chosenProjects
    ) as Set<DataObjectId>
    ids.forEach(projectId => {
      const project = this.box.get(projectId) as Hub
      if (!chosenProjects[projectId]) {
        UserAccess.removeRoleInProject(user.userId, project)
      } else {
        UserAccess.setRoleInProject(
          user.userId,
          project,
          chosenProjects[projectId]
        )
      }
    })

    const wasChosenDeps = this.getDepartmentsForUser(user.userId)
    const depIds = ObjUtil.getChangedKeys(
      wasChosenDeps,
      chosenDepartments
    ) as Set<DataObjectId>
    depIds.forEach(depId => {
      const dep = DepartmentsClient.getDepartment(this.box, depId)
      const role = chosenDepartments[depId] || 'excluded'
      DepartmentsClient.setRole(this.box, user.userId, dep, role)
    })
    this.state.wait = true
    this.close()
  }

  private getProjectsForUser(userId: DataObjectId): {
    [id: string]: PermissionRoleKey
  } {
    const projectsChosen: {[id: string]: PermissionRoleKey} = {}
    this.state.projects.forEach(p => {
      const role = UserAccess.getRoleInProject(userId, p)
      if (role) {
        projectsChosen[p.id] = role
      }
    })
    return projectsChosen
  }

  private getDepartmentsForUser(userId: DataObjectId): {
    [id: string]: 'manager' | 'member'
  } {
    const res: {[id: string]: 'manager' | 'member'} = {}
    this.state.departments.forEach(d => {
      if (d.managers.includes(userId)) {
        res[d.id] = 'manager'
      } else if (d.members.includes(userId)) {
        res[d.id] = 'member'
      }
    })
    return res
  }

  private getPositionForUser(userId: DataObjectId): string {
    const c = this.box.get(this.box.companyId) as Hub
    const eSett = c.data.employeeSettings
      ? c.data.employeeSettings[userId]
      : undefined
    return eSett ? eSett.position : ''
  }

  onScroll(ev: React.UIEvent<HTMLDivElement>): void {
    const el = ev.target as HTMLDivElement
    if (el) {
      if (el.scrollTop) {
        el.classList.add('with-shadow')
      } else {
        el.classList.remove('with-shadow')
      }
    }
  }
}

export const [UserCardContext, useUserCardStore] = createUseStore(UserCardStore)
